import {
  faCheck,
  faCheckDouble,
  faChevronLeft,
  faChevronRight,
  faFlag,
  faImages,
  faPaperPlane,
  faSpinner,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import imageCompression from "browser-image-compression";
import { isSameDay } from "date-fns";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CertificateIcon } from "../../assets/icons";
import { ASSETS } from "../../config/assets";
import { useAlert } from "../../context/AlertContext";
import { useAuth } from "../../context/AuthContext";
import { useChat } from "../../context/ChatContext";
import { trackEvent } from "../../hooks/useAnalytics";
import { useScrollLock } from "../../hooks/useScrollLock";
import { buildUserProfilePath } from "../../utils/userProfile";
import PopupReport from "../PopupReport";
import "./ChatModal.css";
import MessageTimestampDivider from "./MessageTimestampDivider";

// Centrální oddělovač času se zobrazí nad první zprávou, při změně dne
// a při pauze delší než 20 minut.
const TIMESTAMP_GAP_MINUTES = 20;

const shouldShowTimestamp = (currentMsg, prevMsg) => {
  if (!prevMsg) return true;

  const current = new Date(currentMsg.created_at);
  const prev = new Date(prevMsg.created_at);

  if (!isSameDay(current, prev)) return true;

  return (current.getTime() - prev.getTime()) / 60000 > TIMESTAMP_GAP_MINUTES;
};

// Označení kontextu podle typu subjektu
const SUBJECT_LABELS = {
  item: { banner: "Reakce na inzerát" },
  user: { banner: "Reakce na profil" },
};

const MAX_ATTACHMENTS = 10;

// Vzdálenost od horního okraje, při které se načte další část historie
const LOAD_MORE_THRESHOLD = 160;
// Rezerva pro uvolnění příznaku programového scrollu, když prohlížeč
// nepodpoří událost scrollend
const SMOOTH_SCROLL_MS = 700;
// Kolik rámců necháme dorovnat pozici nalezené zprávy
// Klidně dlouho – fotky a písmo se mohou dosadit až po chvíli a každý
// posun obsahu nad zprávou by ji zase odsunul dolů
const MAX_REVEAL_FRAMES = 120;
// Kolik rámců po sobě musí být pozice stejná, než přestaneme dorovnávat
const STABLE_REVEAL_FRAMES = 6;
// Kolik rámců čekáme, než se uzel zprávy znovu objeví (React ho mohl
// překreslit) – pak už to nemá smysl
const MAX_REVEAL_MISSING_FRAMES = 20;
// Kolikrát zkusíme stránku vyžádat, než se něco jiného načítá
const MAX_SEARCH_RETRIES = 180;
// Kolik stránek historie maximálně načteme, než hledanou zprávu najdeme
const MAX_SEARCH_PAGES = 10;

const MAX_PHOTO_SIZE_MB = 8;

const highlightTerm = (text, term) => {
  if (!text || !term) return text;

  const parts = [];
  const lowerText = text.toLowerCase();
  const lowerTerm = term.toLowerCase();
  let cursor = 0;
  let index = lowerText.indexOf(lowerTerm);

  while (index !== -1) {
    if (index > cursor) {
      parts.push(text.slice(cursor, index));
    }

    parts.push(
      <mark key={`${index}-${parts.length}`} className="chat_highlight">
        {text.slice(index, index + term.length)}
      </mark>,
    );

    cursor = index + term.length;
    index = lowerText.indexOf(lowerTerm, cursor);
  }

  if (!parts.length) return text;

  parts.push(text.slice(cursor));

  return parts;
};

// Fotka ve zprávě – kliknutím se otevře zvětšeně v překryvné galerii
const PHOTO_MAX_WIDTH = 260;

// Backend zná rozměry fotky. Pro jednu fotku ve zprávě je přepíšeme do
// atributů width/height, aby prohlížeč rezervoval místo už před načtením
// obrázku a chat po otevření neposkakoval na konec. U mřížky velikost
// určuje CSS, takže tam rozměry nepředepisujeme.
const photoPlaceholderSize = (photo, fixedSize) => {
  if (fixedSize || !photo.width || !photo.height) return {};

  const ratio = photo.width / photo.height;
  const width = Math.round(Math.min(PHOTO_MAX_WIDTH, photo.width));

  return { width, height: Math.round(width / ratio) };
};

const ChatPhoto = ({ photo, fixedSize, onOpen }) => (
  <button
    type="button"
    className="chat_photo"
    onClick={() => onOpen(photo.url)}
    title={photo.name}
  >
    <img
      src={photo.url}
      alt={photo.name}
      decoding="async"
      {...photoPlaceholderSize(photo, fixedSize)}
    />
  </button>
);

// Výchozí chování knihovny propouští běžné kolečko k posunu stránky
// a zoomuje jen ctrl+kolečko. My chceme, aby kolečko zoomovalo vždy.
const interceptAllWheels = () => false;

const ChatGalleryOverlay = ({ photos, activeUrl, onNavigate, onClose }) => {
  const activeIndex = photos.findIndex((photo) => photo.url === activeUrl);
  const hasMultiple = photos.length > 1;
  // Knihovna neaplikuje transform sama – oznamuje jen nový stav přes
  // onUpdate a čeká, že ho zrcadlíme do DOM. Bez toho by kolečko i tažení
  // neudělaly nic viditelného.
  const imgRef = useRef(null);

  const handleUpdate = useCallback(({ x, y, scale: nextScale }) => {
    if (!imgRef.current) return;

    imgRef.current.style.setProperty(
      "transform",
      make3dTransformValue({ x, y, scale: nextScale }),
    );
  }, []);

  const isFirst = activeIndex === 0;
  const isLast = activeIndex === photos.length - 1;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (!hasMultiple) return;
      if (event.key === "ArrowRight" && activeIndex < photos.length - 1)
        onNavigate(1);
      if (event.key === "ArrowLeft" && activeIndex > 0) onNavigate(-1);
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, hasMultiple, onClose, onNavigate, photos.length]);

  useScrollLock(true);

  return (
    <div
      className="loader_container gallery_overlay chat_gallery_overlay"
      onClick={onClose}
    >
      {hasMultiple && (
        <button
          type="button"
          className={`gallery_nav prev ${isFirst ? "disabled" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            onNavigate(-1);
          }}
          disabled={isFirst}
          aria-label="Předchozí fotka"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}

      <div
        className="gallery_content_wrapper"
        onClick={(event) => event.stopPropagation()}
      >
        <QuickPinchZoom
          // Při přepnutí fotky se zoom vrací na 1 – knihovna nemá
          // veřejné API pro reset, proto ji přemountujeme přes key
          key={activeUrl}
          onUpdate={handleUpdate}
          wheelScaleFactor={1000}
          shouldInterceptWheel={interceptAllWheels}
          animationDuration={100}
          minZoom={1}
          maxZoom={5}
        >
          <img
            ref={imgRef}
            src={activeUrl}
            alt="Fotka ve zprávě"
            className="form_img_full"
            onClick={(event) => event.stopPropagation()}
          />
        </QuickPinchZoom>
      </div>

      {hasMultiple && (
        <button
          type="button"
          className={`gallery_nav next ${isLast ? "disabled" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            onNavigate(1);
          }}
          disabled={isLast}
          aria-label="Další fotka"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}
    </div>
  );
};

// Stav odeslání: odesláno (1 fajfka), doručeno (2 šedé), přečteno (2 modré)
const MessageReceipt = ({ message }) => {
  if (message.read_at) {
    return (
      <span className="body_smallest chat_receipt read" title="Přečteno">
        <FontAwesomeIcon icon={faCheckDouble} />
      </span>
    );
  }

  if (message.delivered_at) {
    return (
      <span className="body_smallest chat_receipt" title="Doručeno">
        <FontAwesomeIcon icon={faCheckDouble} />
      </span>
    );
  }

  return (
    <span className="body_smallest chat_receipt" title="Odesláno">
      <FontAwesomeIcon icon={faCheck} />
    </span>
  );
};

const ChatModal = () => {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    activeConversation,
    activeConversationId,
    messages,
    messagesMeta,
    isTyping,
    isOnline,
    isLoadingMessages,
    isSending,
    searchTarget,
    clearSearchTarget,
    messagesByConversation,
    closeChat,
    sendMessage,
    deleteMessage,
    loadOlderMessages,
    notifyTyping,
  } = useChat();

  const [text, setText] = useState("");
  const [confirmingId, setConfirmingId] = useState(null);
  // Zpráva, nad kterou je právě kurzor – u té se ukážou akce
  const [hoveredId, setHoveredId] = useState(null);
  // Přílohy připravené k odeslání (už zkomprimované fotky)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isPreparingFiles, setIsPreparingFiles] = useState(false);
  // Skrytý input pro výběr fotek ze zařízení
  const fileInputRef = useRef(null);
  // Zámek proti dvojímu odeslání mezi dvěma stisknutími Entra
  const isSendingRef = useRef(false);
  // Zvětšená fotka v překryvné galerii
  const [gallery, setGallery] = useState(null);

  // Nahlášení konverzace – admin uvidí celou konverzaci a rozhodne
  const [isReporting, setIsReporting] = useState(false);

  const scrollRef = useRef(null);
  const messageRefs = useRef({});
  const isAtBottomRef = useRef(true);
  const wasTypingRef = useRef(false);
  // Při načítání historie se nesmí přeskočit dolů
  const preserveScrollRef = useRef(false);
  // Pozice scrollu před načtením starších zpráv, aby se pohled neposunul
  const scrollRestoreRef = useRef(null);
  // Kolik stránek historie jsme už přitáhli, než jsme hledanou zprávu našli
  const searchPagesRef = useRef(0);
  // Podle čeho poznáme, že se právě otevřela jiná konverzace
  const previousConversationId = useRef(null);
  // Rozlišuje náš vlastní scroll od uživatelského – hladký přesun na
  // nalezenou zprávu generuje desítky událostí a bez toho by každá
  // z nich mohla spustit načítání starší historie
  const programmaticScrollRef = useRef(false);
  // Po odhalení hledané zprávy se nesmí dohrát další starší stránka, dokud
  // uživatel nezačne scrollovat sám – její obsah by přišel nad zprávu
  // a posunul ji dolů
  const awaitingUserScrollRef = useRef(false);
  const searchRevealRef = useRef(null);
  // Kolikrát se nepodařilo stránku vyžádat (něco jiného se načítá)
  const searchRetriesRef = useRef(0);
  // Připočet pro vynucení běhu efektu, když se stránku nepodařilo
  // požadovat napoprvé (třeba už běží jiné načítání)
  const [searchRetry, setSearchRetry] = useState(0);
  const isOpen = Boolean(activeConversation);
  const counterpart = activeConversation?.counterpart;
  const counterpartIsOnline = isOnline(counterpart?.id);
  const searchTerm = searchTarget?.term || "";
  const targetMessageId = searchTarget?.messageId || null;

  // Reakce se zobrazí nad zprávou, ke které patří; sirotky (mimo načtené stránky) vynecháme
  // Všechny fotky v otevřené konverzaci – slouží k listování v překryvné galerii
  const galleryPhotos = useMemo(() => {
    if (!gallery) return [];

    return (messagesByConversation[gallery.conversationId] || []).flatMap(
      (message) => message.attachments || [],
    );
  }, [gallery, messagesByConversation]);

  const openGallery = useCallback((conversationId, url) => {
    setGallery({ conversationId, url });
  }, []);

  const navigateGallery = useCallback(
    (direction) => {
      setGallery((current) => {
        if (!current) return current;

        const photos = (
          messagesByConversation[current.conversationId] || []
        ).flatMap((message) => message.attachments || []);
        const index = photos.findIndex((photo) => photo.url === current.url);
        const next = photos[index + direction];

        return next ? { ...current, url: next.url } : current;
      });
    },
    [messagesByConversation],
  );

  const contextByMessageId = useMemo(() => {
    const map = {};

    messages.forEach((message) => {
      // Reakce patří k této zprávě nad textem – v chatu se vykreslí jako
      // hlavička nad bublinou, ne jako vlastní zpráva v seznamu.
      // Smazanou reakci přeskočíme, uživatel ji už neuvidí.
      if (
        message.is_system &&
        !message.is_deleted &&
        message.context_for_message_id
      ) {
        map[message.context_for_message_id] = message;
      }
    });

    return map;
  }, [messages]);

  const visibleMessages = useMemo(
    () => messages.filter((message) => !message.context_for_message_id),
    [messages],
  );

  useScrollLock(isOpen);

  // Uživatel je na konci seznamu, když je od spodního okraje vzdálen méně než 80 px
  const isScrolledToBottom = () => {
    const el = scrollRef.current;

    if (!el) return true;

    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // Označí scroll jako náš vlastní, aby jeho události nespustily načítání
  // starší historie. Hladký scroll běží dlouho, ten se uvolní po události
  // scrollend (rezerva, když ji prohlížeč nemá). Okamžitý scroll je hotový
  // hned, uvolníme ho v dalším rámci.
  const runProgrammaticScroll = (scroll, smooth = false) => {
    const el = scrollRef.current;

    programmaticScrollRef.current = true;
    scroll();

    if (!el) {
      programmaticScrollRef.current = false;

      return;
    }

    const release = () => {
      programmaticScrollRef.current = false;
    };

    if (!smooth) {
      window.requestAnimationFrame(release);

      return;
    }

    const timer = window.setTimeout(release, SMOOTH_SCROLL_MS);

    el.addEventListener(
      "scrollend",
      () => {
        window.clearTimeout(timer);
        release();
      },
      { once: true },
    );
  };

  // Čeká se na odpověď protistrany – dokud je v konverzaci jen vlastní zpráva
  // (systémová zpráva o kontextu se do toho počítání nezapočítává)
  const ownMessages = visibleMessages.filter(
    (message) => message.user_id === user?.id,
  );
  const awaitingReply = ownMessages.length === 1;

  const handleOpenProfile = (event) => {
    event?.stopPropagation();

    if (!counterpart?.id) return;

    closeChat();
    navigate(buildUserProfilePath(counterpart), {
      state: {
        ...location.state,
        userName: `${counterpart.first_name} ${counterpart.last_name}`,
      },
    });
  };

  const hasOlder = messagesMeta
    ? messagesMeta.current_page < messagesMeta.last_page
    : false;

  // scrollIntoView nescrollne úplně na konec – zůstává pár pixelů, proto
  // nastavujeme pozici přímo.
  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;

    if (!el) return;

    runProgrammaticScroll(() => {
      // "auto" by znamenal "použij scroll-behavior z CSS", který je
      // globálně smooth – posun by se rozjel animací a další scroll
      // by ho přerušil uprostřed. "instant" CSS přebije vždy.
      if (!smooth && typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "instant" });

        return;
      }

      if (typeof el.scrollTo === "function") {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: smooth ? "smooth" : "instant",
        });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }, smooth);
  }, []);

  // Nastaví scroll tak, aby zpráva byla přesně uprostřed. Počítáme z
  // obdélníků místo scrollIntoView, protože ten neuhlídá, kam přesně
  // míří, když se mezitím seskupí obsah nad zprávou.
  const centerMessageInView = useCallback((node) => {
    const el = scrollRef.current;

    if (!el) return false;

    const nodeRect = node.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const nodeOffsetTop = el.scrollTop + (nodeRect.top - elRect.top);
    const desired = nodeOffsetTop - (el.clientHeight - nodeRect.height) / 2;
    const max = Math.max(0, el.scrollHeight - el.clientHeight);

    el.scrollTop = Math.min(Math.max(0, desired), max);

    return el.scrollTop;
  }, []);

  // Nalezenou zprávu dáme doprostřed a pár dalších rámců jí držíme
  // pozici, dokud se neshodne – obsah nad ní se ještě může dodatečně
  // seskupit (fotky, načtené starší zprávy) a posunul by ji zase dolů.
  const revealMessage = useCallback(
    (messageId, conversationId) => {
      let previous = null;
      let frames = 0;
      let stable = 0;
      let missing = 0;

      const step = () => {
        if (searchRevealRef.current?.messageId !== messageId) return;
        if (!scrollRef.current) return;

        // Uzel hledáme pokaždé znovu podle id. Když držíme odkaz na
        // starý uzel, React při překreslení řádku vytvoří nový, náš
        // odkaz zůstane odpojený a dorovnávání skončí – zpráva pak
        // skončí kdekoliv.
        const node = messageRefs.current[messageId];

        if (!node) {
          missing += 1;

          if (missing <= MAX_REVEAL_MISSING_FRAMES) {
            window.requestAnimationFrame(step);
          } else {
            searchRevealRef.current = null;
          }

          return;
        }

        missing = 0;

        const position = centerMessageInView(node);

        frames += 1;

        if (previous !== null && Math.abs(previous - position) < 1) {
          stable += 1;
        } else {
          stable = 0;
        }

        previous = position;

        // Končíme, až je pozice stabilní po sobě několik rámců
        if (frames < MAX_REVEAL_FRAMES && stable < STABLE_REVEAL_FRAMES) {
          window.requestAnimationFrame(step);

          return;
        }

        searchRevealRef.current = null;
      };

      searchRevealRef.current = { messageId, conversationId };
      window.requestAnimationFrame(step);
    },
    [centerMessageInView],
  );

  // Historii po odhalení dohrajeme až když uživatel začne scrollovat sám.
  // Scroll událost sama nestačí – tu vyvolají i naše vlastní posuny a
  // dosednutí obsahu.
  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return undefined;

    const release = () => {
      awaitingUserScrollRef.current = false;
    };

    el.addEventListener("wheel", release, { passive: true });
    el.addEventListener("touchmove", release, { passive: true });
    el.addEventListener("pointerdown", release);
    window.addEventListener("keydown", release);

    return () => {
      el.removeEventListener("wheel", release);
      el.removeEventListener("touchmove", release);
      el.removeEventListener("pointerdown", release);
      window.removeEventListener("keydown", release);
    };
  }, [isOpen]);

  // Scroll na konec udržujeme jen tehdy, když na něm uživatel je – jinak by
  // mu překážel do čtení historie.
  const stickToBottom = useCallback(() => {
    if (preserveScrollRef.current) return;

    isAtBottomRef.current = true;
    scrollToBottom();
  }, [scrollToBottom]);

  // Po vykreslení starších zpráv vrátíme pohled na stejné místo
  useLayoutEffect(() => {
    const restore = scrollRestoreRef.current;

    if (!restore) return;

    const el = scrollRef.current;

    scrollRestoreRef.current = null;
    preserveScrollRef.current = false;

    if (!el) return;

    el.scrollTop = restore.top + (el.scrollHeight - restore.height);
  }, [messages.length]);

  // Hned na poslední zprávu při otevření konverzace a při každé nové zprávě.
  // Každá přidaná zpráva změní messages.length, takže podmínku netřeba hlídat
  // extra. Běží v useLayoutEffect, aby se posun stihl udělat ještě před
  // vykreslením – uživatel tak nevidí postupné poskakování.
  useLayoutEffect(() => {
    const openedConversation =
      previousConversationId.current !== activeConversationId;

    previousConversationId.current = activeConversationId;

    if (!isOpen || isLoadingMessages || messages.length === 0) return;
    // Při hledání má přednost scroll na nalezenou zprávu.
    if (targetMessageId) return;
    // Při načítání starší historie se scroll drží na místě.
    if (scrollRestoreRef.current) return;

    // Otevření konverzace i nová zpráva posunou na konec, ale jen když tam
    // uživatel je – jinak by mu to překáželo při čtení historie.
    if (!openedConversation && !isAtBottomRef.current) return;

    // Kontejner se mezi otevřeními znovu používá a drží si pozici
    // předchozí konverzace – proto ji před měřením výšky zahodíme,
    // jinak by se scroll ustálil na nějakém zděděném místě.
    if (openedConversation && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }

    stickToBottom();
  }, [
    isOpen,
    isLoadingMessages,
    activeConversationId,
    messages.length,
    targetMessageId,
    stickToBottom,
    scrollToBottom,
  ]);

  // Záloha pro případ, že se přesto něco rozměrově změní (jiný font,
  // dlouhý text přeložený až po vykreslení) – držíme konec jen když
  // na něm uživatel je.
  useEffect(() => {
    const el = scrollRef.current;

    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      if (!isOpen || !isAtBottomRef.current) return;

      stickToBottom();
    });

    observer.observe(el);

    for (const child of el.children) observer.observe(child);

    return () => observer.disconnect();
  }, [isOpen, messages.length, activeConversationId, stickToBottom]);

  // Když protistrana začne psát a uživatel je dole, posune se znovu dolů
  useEffect(() => {
    const startedTyping = isTyping && !wasTypingRef.current;

    wasTypingRef.current = isTyping;

    if (!isOpen || !startedTyping || !isAtBottomRef.current) return;

    scrollToBottom(true);
  }, [isTyping, isOpen, scrollToBottom]);

  // Přesun na zprávu, ve které se nachází hledané slovo
  useEffect(() => {
    if (!isOpen || !targetMessageId) {
      searchPagesRef.current = 0;
      searchRetriesRef.current = 0;

      return;
    }

    const node = messageRefs.current[targetMessageId];

    if (!node) {
      // Hledaná zpráva může být na starší stránce, která ještě není
      // načtená. Dotáhneme další kus historie; efekt se po jejím
      // načtení spustí znovu, protože hlídá messages.length.
      if (!hasOlder || isLoadingMessages) return;

      // Zbytečně dlouhé hledání by načítalo celou konverzaci – when
      // message in the conversation no longer exists (e.g. smazaná).
      if (searchPagesRef.current >= MAX_SEARCH_PAGES) {
        clearSearchTarget();

        return;
      }

      scrollRestoreRef.current = null;

      const started = loadOlderMessages(activeConversation.id);

      if (started) {
        // Skutečně načtená stránka se počítá do rozpočtu
        searchPagesRef.current += 1;
        searchRetriesRef.current = 0;

        return;
      }

      // Stránku se nepodařilo požadovat – třeba už běží jiné načítání.
      // Bez opakování by hledání uvízlo, protože efekt nemá žádnou
      // změnu, na kterou by se znovu spustil.
      searchRetriesRef.current += 1;

      if (searchRetriesRef.current > MAX_SEARCH_RETRIES) {
        clearSearchTarget();

        return;
      }

      window.requestAnimationFrame(() => setSearchRetry((value) => value + 1));

      return;
    }

    searchPagesRef.current = 0;
    scrollRestoreRef.current = null;

    awaitingUserScrollRef.current = true;

    // Přesun je záměrně okamžitý a přesně dopočítaný – hladký animovaný
    // přesun dlouhým seznamem končí jinde, ne kam mířil.
    runProgrammaticScroll(() =>
      revealMessage(targetMessageId, activeConversationId),
    );
    isAtBottomRef.current = false;
    clearSearchTarget();
  }, [
    isOpen,
    targetMessageId,
    messages.length,
    hasOlder,
    isLoadingMessages,
    activeConversation,
    loadOlderMessages,
    clearSearchTarget,
    revealMessage,
    activeConversationId,
    searchRetry,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setText("");
      setConfirmingId(null);
      // Bez resetu by se popup nahlášení znovu otevřel v další konverzaci
      setIsReporting(false);
    }
  }, [isOpen]);

  // Při přepnutí konverzace musí jít nahlášení o jiné, ne o předchozí
  useEffect(() => {
    setIsReporting(false);
  }, [activeConversationId]);

  // Při změně konverzace zahodíme zálohu pozice scrollu – vzala by se
  // z předchozí konverzace a posunula by pohled jinam
  useEffect(() => {
    scrollRestoreRef.current = null;
    preserveScrollRef.current = false;
    isAtBottomRef.current = true;

    // Dorovnávání nalezené zprávy se ukončí jen když patří jiné
    // konverzaci – jinak by ho efekt zručil hned po rozjetí
    if (searchRevealRef.current?.conversationId !== activeConversationId) {
      searchRevealRef.current = null;
      awaitingUserScrollRef.current = false;
    }
  }, [activeConversationId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Escape zavírá nejdřív popup nahlášení, ne celý chat
      if (event.key === "Escape" && isReporting) {
        setIsReporting(false);

        return;
      }

      if (event.key === "Escape" && isOpen) closeChat();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeChat, isReporting]);

  // Načítání starších zpráv: uživatel musí scrollovat výrazně nahoru, jinak
  // by se stránka načítala při každém malém pohybu
  const handleScroll = (event) => {
    // Události z našeho vlastního scrollu nesmějí spustit načítání
    // historie – hladký přesun na nalezenou zprávu by se tak sám sobě
    // posunul jinam
    if (programmaticScrollRef.current) return;

    isAtBottomRef.current = isScrolledToBottom();

    // Po odhalení hledané zprávy počkáme na skutečný uživatelský scroll,
    // jinak by doplněná starší zpráva hledanou odsunula dolů
    if (awaitingUserScrollRef.current) return;

    if (event.target.scrollTop > LOAD_MORE_THRESHOLD || !hasOlder) return;

    // Během načítání zpráv je scroll ještě neusazený – uložená pozice by
    // pak platila pro jinou výšku kontejneru a chat by po načtení skočil.
    if (isLoadingMessages) return;

    // Když už se starší zprávy načítat nemusí, pozici scrollu vůbec
    // nezachováváme – visela by pak a při příští zprávě by skočila jinam.
    const started = loadOlderMessages(activeConversation.id);

    if (!started) return;

    // Výšku i pozici si uložíme a rozdíl doplní až efekt po vykreslení
    // nových zpráv – dřív by byl výškový rozdíl ještě nulový.
    preserveScrollRef.current = true;

    scrollRestoreRef.current = {
      height: event.target.scrollHeight,
      top: event.target.scrollTop,
    };
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (
      (!text.trim() && pendingFiles.length === 0) ||
      isSending ||
      isPreparingFiles
    )
      return;

    // isSending se překreslí až po dalším renderu – bez tohoto zámku by se
    // dvě rychlé stisknutí Entra poslaly jako dvě zprávy
    if (isSendingRef.current) return;

    isSendingRef.current = true;

    const photos = pendingFiles;
    const body = text;

    setText("");
    setPendingFiles([]);

    // Object URL se uvolní až po nahrání – do té doby je náhled v optimistické zprávě
    try {
      await sendMessage(
        body,
        photos.map((photo) => ({ file: photo.file, url: photo.url })),
      );

      trackEvent("message_sent", {
        conversation_id: activeConversationId,
        has_attachments: photos.length > 0,
      });
    } finally {
      isSendingRef.current = false;
    }
  };

  // Připraví přílohu k odeslání – fotky zkomprimuje, dokumenty nechá v původním stavu
  const handleFilesSelected = async (event) => {
    const selected = Array.from(event.target.files || []);

    event.target.value = "";

    await addPhotos(selected);
  };

  const prepareAttachment = async (file) => {
    if (!file.type.startsWith("image/")) {
      showAlert("error", `Soubor „${file.name}" není fotka.`);

      return null;
    }

    if (file.size > MAX_PHOTO_SIZE_MB * 1024 * 1024) {
      showAlert(
        "error",
        `Fotka „${file.name}" je větší než ${MAX_PHOTO_SIZE_MB} MB.`,
      );

      return null;
    }

    const id = `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;

    // GIF by komprese rozbila, posílá se beze změny
    if (file.type === "image/gif") {
      return { id, file, url: URL.createObjectURL(file) };
    }

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
      });

      return { id, file: compressed, url: URL.createObjectURL(compressed) };
    } catch (err) {
      console.error("Komprese fotky se nezdařila, posílá se originál:", err);

      return { id, file, url: URL.createObjectURL(file) };
    }
  };

  // Fotky se přidávají výběrem ze zařízení
  const addPhotos = useCallback(
    async (selected) => {
      if (!selected.length) return;

      const room = MAX_ATTACHMENTS - pendingFiles.length;

      if (room <= 0) {
        showAlert("error", `Lze přiložit nejvýše ${MAX_ATTACHMENTS} fotek.`);
        return;
      }

      const batch = selected.slice(0, room);

      if (batch.length < selected.length) {
        showAlert("error", `Lze přiložit nejvýše ${MAX_ATTACHMENTS} fotek.`);
      }

      setIsPreparingFiles(true);

      try {
        const prepared = await Promise.all(batch.map(prepareAttachment));

        setPendingFiles((prev) => [...prev, ...prepared.filter(Boolean)]);
      } catch (err) {
        console.error("Chyba při zpracování příloh:", err);
        showAlert("error", "Fotky se nepodařilo načíst.");
      } finally {
        setIsPreparingFiles(false);
      }
    },
    [pendingFiles.length, showAlert],
  );

  const handleDelete = async (messageId) => {
    setConfirmingId(null);
    await deleteMessage(messageId);
  };

  if (!isOpen) return null;

  return (
    <>
      {gallery && (
        <ChatGalleryOverlay
          photos={galleryPhotos}
          activeUrl={gallery.url}
          onNavigate={navigateGallery}
          onClose={() => setGallery(null)}
        />
      )}

      <div className="chat_modal_overlay" onClick={closeChat}>
        <div
          className="chat_modal"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="chat_modal_header">
            <button
              type="button"
              className="chat_modal_identity"
              onClick={handleOpenProfile}
              title="Otevřít profil uživatele"
            >
              <span className="chat_modal_avatar_wrap">
                <img
                  className="chat_modal_avatar"
                  src={counterpart?.profile_image_url || ASSETS.default_avatar}
                  alt={counterpart?.first_name || "avatar"}
                />
                {counterpartIsOnline && (
                  <span
                    className="chat_modal_online_dot"
                    title="Online"
                    aria-label="Online"
                  />
                )}
              </span>
              <span className="chat_modal_names">
                <span className="body_base strong chat_modal_name">
                  {counterpart?.first_name} {counterpart?.last_name}
                  {counterpart?.is_verified && <CertificateIcon className="certificate_icon--normal" />}
                </span>
                {isTyping && (
                  <span className="body_smallest chat_modal_status">
                    <span className="chat_typing_label">
                      {counterpart?.first_name} píše
                      <span className="chat_typing_dots" aria-hidden="true">
                        <i />
                        <i />
                        <i />
                      </span>
                    </span>
                  </span>
                )}
                <span className="body_smallest chat_modal_specialization">
                  {counterpart?.specializations_label || "Člen komunity"}
                </span>
              </span>
            </button>

            {activeConversationId && (
              <button
                type="button"
                className="chat_modal_report"
                onClick={() => setIsReporting(true)}
                title="Nahlásit tuto konverzaci"
                aria-label="Nahlásit tuto konverzaci"
              >
                <FontAwesomeIcon icon={faFlag} />
              </button>
            )}
          </div>

          <div
            className="chat_modal_body"
            ref={scrollRef}
            onScroll={handleScroll}
          >
            {isLoadingMessages && messages.length === 0 && (
              <div className="chat_modal_state">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="chat_modal_spinner"
                />
                <p className="body_small">Načítání zpráv…</p>
              </div>
            )}

            {!isLoadingMessages && messages.length === 0 && (
              <div className="chat_modal_state">
                <p className="body_small">
                  Zatím žádné zprávy
                </p>
              </div>
            )}

            {hasOlder && (
              <p className="body_smallest chat_modal_load_older">
                {isLoadingMessages
                  ? "Načítání…"
                  : "Při scrollu nahoru se načtou starší zprávy"}
              </p>
            )}

            {visibleMessages.map((message, index) => {
              const isMine = message.user_id === user?.id;
              const previous = visibleMessages[index - 1];
              const showTimestamp =
                !message.is_deleted && shouldShowTimestamp(message, previous);
              const grouped =
                previous &&
                previous.user_id === message.user_id &&
                !showTimestamp;
              const isLastMine =
                isMine &&
                !visibleMessages
                  .slice(index + 1)
                  .some((m) => m.user_id === user?.id);
              const reaction = contextByMessageId[message.id];
              const isTarget = targetMessageId === message.id;

              return (
                <div key={message.id}>
                  {showTimestamp && (
                    <MessageTimestampDivider date={message.created_at} />
                  )}

                  <div
                    className={`chat_row ${isMine ? "own" : "other"} ${grouped ? "grouped" : ""} ${isTarget ? "chat_row_target" : ""} ${hoveredId === message.id ? "chat_row_hovered" : ""}`}
                    ref={(node) => {
                      if (node) {
                        messageRefs.current[message.id] = node;
                      } else {
                        delete messageRefs.current[message.id];
                      }
                    }}
                    onMouseEnter={() => setHoveredId(message.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    {!isMine && !grouped && (
                      <Link
                        className="chat_row_avatar_link"
                        to={buildUserProfilePath(message.sender)}
                        onClick={() => closeChat()}
                        title="Otevřít profil uživatele"
                      >
                        <img
                          className="chat_row_avatar"
                          src={
                            message.sender?.profile_image_url ||
                            ASSETS.default_avatar
                          }
                          alt={message.sender?.first_name || "avatar"}
                        />
                      </Link>
                    )}
                    {!isMine && grouped && (
                      <span className="chat_row_avatar_placeholder" />
                    )}

                    <div className="chat_content">
                      <div
                        className="chat_bubble_wrap"
                        title={new Date(message.created_at).toLocaleString(
                          "cs-CZ",
                        )}
                      >
                        {/* Reakce na inzerát/profil – patří k této zprávě */}
                        {reaction?.context && (
                          <a
                            className="chat_reaction_link"
                            href={reaction.context.url}
                            onClick={(event) => {
                              // Navigace musí přijít až po zavření chatu.
                              // <Link> totiž v onClicku naviguje
                              // synchronně a closeChat() odkaz v tu chvíli
                              // ještě odmontuje – přechod se neproběhl.
                              event.preventDefault();
                              closeChat();
                              navigate(reaction.context.url);
                            }}
                          >
                            <div className="chat_reaction">
                              <span className="chat_reaction_label">
                                📌{" "}
                                {SUBJECT_LABELS[reaction.context.subject_type]
                                  ?.banner || "Reakce"}
                                :
                              </span>{" "}
                              <span className="chat_reaction_title">
                                {reaction.context.title}
                              </span>
                            </div>
                          </a>
                        )}

                        {/* Smazaná zpráva zůstane v chatu jako hláška */}
                        {message.is_deleted && (
                          <div className="chat_bubble chat_bubble_deleted">
                            <p className="chat_bubble_text body_smallest strong">
                              Zpráva smazána
                            </p>
                          </div>
                        )}

                        {/* Fotky a text tvoří dvě samostatné bubliny,
                                                jako kdyby se posílaly dvěma zprávami */}
                        {!message.is_deleted &&
                          message.attachments?.length > 0 && (
                            <div className="chat_bubble chat_bubble_photos">
                              <div
                                className={`chat_attachments chat_attachments_${message.attachments.length > 1 ? "grid" : "single"}`}
                              >
                                {message.attachments.map((attachment) => (
                                  <ChatPhoto
                                    key={attachment.id ?? attachment.url}
                                    photo={attachment}
                                    fixedSize={message.attachments.length > 1}
                                    onOpen={(url) =>
                                      openGallery(message.conversation_id, url)
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        {!message.is_deleted && message.body && (
                          <div className="chat_bubble">
                            <p className="chat_bubble_text">
                              {highlightTerm(message.body, searchTerm)}
                            </p>
                          </div>
                        )}
                      </div>

                      {isMine && !message.is_pending && !message.is_deleted && (
                        <div
                          className={`chat_actions ${
                            confirmingId === message.id ? "confirming" : ""
                          }`}
                        >
                          {confirmingId === message.id ? (
                            <>
                              <span className="body_smallest">
                                Opravdu smazat?
                              </span>
                              <button
                                type="button"
                                onClick={() => handleDelete(message.id)}
                              >
                                Ano
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmingId(null)}
                              >
                                Ne
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmingId(message.id)}
                              aria-label="Smazat zprávu"
                              title="Smazat zprávu"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stav zprávy je až pod bublinkou, aby akce zůstaly
                            přesně zarovnané na její střed */}
                  {!message.is_deleted && (message.is_edited || isMine) && (
                    <div className="chat_meta">
                      {message.is_edited && (
                        <span className="body_smallest chat_edited">
                          upraveno
                        </span>
                      )}
                      {isMine && isLastMine && (
                        <MessageReceipt message={message} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Animace "protistrana píše…" – obnovuje se whisperem každých 2,5 s */}
            {isTyping && (
              <div className="chat_row other typing_row">
                <img
                  className="chat_row_avatar"
                  src={counterpart?.profile_image_url || ASSETS.default_avatar}
                  alt={counterpart?.first_name || "avatar"}
                />
                <div
                  className="chat_bubble typing"
                  role="status"
                  aria-label={`${counterpart?.first_name || "Uživatel"} píše`}
                >
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
              </div>
            )}
          </div>

          {awaitingReply && (
            <p className="chat_modal_notice body_smallest">
              Čekáte na odpověď. Další zprávu můžete odeslat, až{" "}
              {counterpart?.first_name} odpoví.
            </p>
          )}

          <form className="chat_composer" onSubmit={handleSubmit}>
            {pendingFiles.length > 0 && (
              <div className="chat_pending_photos">
                {pendingFiles.map((photo) => (
                  <div key={photo.id} className="chat_pending_photo">
                    <img src={photo.url} alt={photo.file.name} />
                    <button
                      type="button"
                      className="chat_pending_photo_remove"
                      onClick={() =>
                        setPendingFiles((prev) => {
                          URL.revokeObjectURL(
                            prev.find((item) => item.id === photo.id)?.url,
                          );

                          return prev.filter((item) => item.id !== photo.id);
                        })
                      }
                      aria-label={`Odebrat ${photo.file.name}`}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="chat_composer_file_input"
              onChange={handleFilesSelected}
            />
            <button
              type="button"
              className="chat_composer_attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={awaitingReply || isPreparingFiles}
              aria-label="Přiložit fotku"
              title="Přiložit fotku"
            >
              <FontAwesomeIcon icon={isPreparingFiles ? faSpinner : faImages} />
            </button>
            <textarea
              className="chat_composer_input"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                notifyTyping();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder={
                awaitingReply ? "Čeká se na odpověď…" : "Napište zprávu…"
              }
              disabled={awaitingReply}
              maxLength={2000}
              rows={1}
            />
            <button
              type="submit"
              className="chat_composer_send"
              disabled={
                awaitingReply ||
                (!text.trim() && pendingFiles.length === 0) ||
                isSending ||
                isPreparingFiles
              }
              aria-label="Odeslat zprávu"
              title="Odeslat zprávu"
            >
              <FontAwesomeIcon icon={isSending ? faSpinner : faPaperPlane} />
            </button>
          </form>
        </div>
      </div>

      {/*
        Nahlášení se renderuje mimo DOM chatu. .chat_modal má animaci
        končící transformem, a tím se stává containing block pro
        position: fixed – popup uvnitř se pak nedostal na obrazovku
        a overflow: hidden ho navíc ořízl. V body je bezpečný.
      */}
      {createPortal(
        <PopupReport
          isOpen={isReporting}
          onClose={() => setIsReporting(false)}
          targetId={activeConversationId}
          type="conversations"
        />,
        document.body,
      )}
    </>
  );
};

export default ChatModal;
